/**
 * Minimal API access for Phase 1.
 *
 * The full typed client (one RTK Query `createApi` with injected endpoints, single-flight
 * 401 refresh, CSRF header) arrives in Phases 2–3. This exists only to prove the
 * frontend-to-backend round trip, which is Phase 1's exit criterion.
 */

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

export interface HealthPayload {
  status: 'ok' | 'degraded';
  db: 'up' | 'down';
  uptimeSeconds: number;
}

export type HealthResult =
  | { state: 'ok'; data: HealthPayload }
  | { state: 'degraded'; data: HealthPayload }
  | { state: 'unconfigured' }
  | { state: 'unreachable'; message: string };

export async function fetchHealth(): Promise<HealthResult> {
  if (!API_BASE_URL) return { state: 'unconfigured' };

  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/health`, {
      credentials: 'include',
    });
    const body = (await res.json()) as { data: HealthPayload };
    return res.ok ? { state: 'ok', data: body.data } : { state: 'degraded', data: body.data };
  } catch (err) {
    return {
      state: 'unreachable',
      message: err instanceof Error ? err.message : 'Request failed',
    };
  }
}
